// Display formatting, and nothing more: integer cents become a dollar string by exact integer
// arithmetic (no float, no rounding), and a period token becomes its month's name. Nothing here
// adds, nets or derives an amount — every figure a page shows is one the engine served.

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** `1234567` → `$12,345.67`; a negative reads `−$12,345.67` (a true minus sign). */
export function money(cents: number): string {
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const dollars = Math.trunc(abs / 100);
  const rest = abs - dollars * 100;
  const text = `$${groupThousands(String(dollars))}.${String(rest).padStart(2, '0')}`;
  return negative ? `−${text}` : text;
}

/** A zero reads as a quiet dash, never as a blank (a blank reads as "not served"). */
export function moneyOrDash(cents: number): string {
  return cents === 0 ? '—' : money(cents);
}

/** A served decimal STRING (`"3840.00"`), grouped for display without ever becoming a number. */
export function decimalMoney(text: string): string {
  const negative = text.startsWith('-');
  const [whole = '0', fraction = '00'] = text.replace(/^-/, '').split('.');
  return `${negative ? '−' : ''}$${groupThousands(whole)}.${fraction.padEnd(2, '0')}`;
}

/** `2026-10` → `October 2026`. */
export function periodName(period: string): string {
  const [year, month] = period.split('-');
  return `${MONTHS[Number(month) - 1] ?? month} ${year}`;
}

/** A month alone: `2026-10` → `October`. */
export function monthName(period: string): string {
  return MONTHS[Number(period.split('-')[1]) - 1] ?? period;
}

/** A served first/last pair as words: one month, `January – June 2026`, or across years. */
export function spanName(
  first: string | null | undefined,
  last: string | null | undefined,
): string {
  if (!last) return '';
  if (!first || first === last) return periodName(last);
  const [firstYear] = first.split('-');
  const [lastYear] = last.split('-');
  if (firstYear === lastYear) return `${monthName(first)} – ${periodName(last)}`;
  return `${periodName(first)} – ${periodName(last)}`;
}

/** The month a fiscal year ends in, as words: `6` → `June`. */
export function fiscalYearEndWords(month: number): string {
  return MONTHS[month - 1] ?? String(month);
}

/** `2026-09-30` → `Sep 30, 2026`. */
export function dateShort(iso: string): string {
  const [year, month, day] = iso.split('-');
  return `${(MONTHS[Number(month) - 1] ?? String(month)).slice(0, 3)} ${Number(day)}, ${year}`;
}

/** A served count or chain position, grouped: `1284` → `1,284`. */
export function count(value: number): string {
  return groupThousands(String(value));
}

/** Words joined as a reader says a list: `a`, `a and b`, `a, b and c`. */
export function listWords(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
}
